import * as React from "react";
import { Button, Divider, Form } from "semantic-ui-react";
import * as moment from "moment";
import { connect } from "react-redux";

import { MedicalType } from "@teamprevent/common/healthSurveillance/models";

import { MedicalOutcomes, TaskType } from "../../task/model";
import { ImmunisationType } from "../../crud/immunisationType";
import { ImmunisationTypeTask } from "../../crud/immunisationTypeTask";
import { AddNextTaskFormModel } from "../models";
import { getUserSortableName } from "../../crud/adminUser/model";
import { AdminUserListModelWithTeams, AppState as AdminUserListModelWithTeamsState } from "../../crud/adminUserWithTeams/model";
import { loadAdminUsersWithTeams as loadAdminUsersAction } from "../../crud/adminUserWithTeams";
import { adminUsersWithTeamsSelector } from "../../crud/adminUserWithTeams/selectors";

import { AddTaskForm } from "./addTaskForm";

interface Props {
    onChange: (tasks: AddNextTaskFormModel[]) => void;
    taskTypes: TaskType[];
    immunisationTypes?: ImmunisationType[];
    immunisationTypeTasks?: ImmunisationTypeTask[];
    ohCaseId: string;
    singleTask?: boolean;
    defaultDueDate?: boolean;
    showErrors: boolean;
    medicalTypes?: MedicalType[];
    caseType?: string;
    savedTasks?: AddNextTaskFormModel[];
    forceSavedTasksUpdate?: boolean;
    onSavedTasksUpdate?: (updated: boolean) => void;
    updateTasks?: AddNextTaskFormModel[];
    forceTasksUpdate?: boolean;
    onTasksUpdate?: (updated: boolean) => void;
    hideAssignmentOption?: boolean;
    assignmentOptionRequired?: boolean;
    medicalOutcomes?: MedicalOutcomes;
    filterMedicalTypes?: boolean;
}

interface State {
    tasks: AddNextTaskFormModel[];
}

interface StateProps {
    adminUsersWithTeams: AdminUserListModelWithTeams[];
}

interface DispatchProps {
    loadAdminUsers: () => void;
}

class AddTasksInputsUnconnected extends React.Component<Props & StateProps & DispatchProps, State> {
    constructor(props: Props & StateProps & DispatchProps) {
        super(props);

        if (!this.props.hideAssignmentOption && !this.props.adminUsersWithTeams?.length) {
            this.props.loadAdminUsers();
        }

        this.addTask = this.addTask.bind(this);
        this.state = {
            tasks: props.savedTasks?.length > 0
                ? props.savedTasks!.map(t => ({
                    task: { ...t.task, dueDate: t.task.dueDate ? moment(t.task.dueDate) : null },
                    isValid: t.isValid
                }))
                : [{ task: { taskCode: null, taskType: null, dueDate: null, notes: null }, isValid: false }]
        };
    }

    public componentDidUpdate() {
        if (this.props.savedTasks &&
            this.props.savedTasks?.length > 0 &&
            this.props.forceSavedTasksUpdate) {

            this.setTasks(this.props.savedTasks, false);
        }
        else if (this.props.updateTasks &&
            this.props.updateTasks?.length > 0 &&
            this.props.forceTasksUpdate) {

            this.setTasks(this.props.updateTasks, true);
        }
    }

    private setTasks(tasksToSet: AddNextTaskFormModel[], updateCallBack: boolean) {
        this.setState({
            tasks: tasksToSet.map(t => ({
                task: {
                    ...t.task,
                    dueDate: t.task.dueDate ? moment(t.task.dueDate) : null,
                    taskType: {
                        ...t.task?.taskType,
                        subTasks: t.task?.taskCode ?
                            this.props.taskTypes?.find(tt => tt.typeId === t.task?.taskType?.typeId)?.subTasks :
                            t.task?.taskType?.subTasks
                    },
                    taskCode: t.task?.taskCode &&
                        this.props.taskTypes?.
                            find(tt => tt.typeId === t.task?.taskType?.typeId)?.
                            subTasks?.find(subTaskId => {
                                return subTaskId === t.task?.taskCode;
                            }) ?
                        t.task.taskCode :
                        null
                },
                isValid: t.isValid
            }))
        }, () => updateCallBack ? this.onTasksUpdate() : this.onSavedTasksUpdate());
    }

    private getAdminUserOptions() {
        if (!this.props.adminUsersWithTeams?.length) {
            return [];
        }

        const adminUsersWithTeams = [...this.props.adminUsersWithTeams.sort((a, b) => getUserSortableName(a).localeCompare(getUserSortableName(b)))];

        adminUsersWithTeams.splice(0, 0, { id: "Unassigned", emailAddress: "Unassigned" });

        return adminUsersWithTeams;
    }

    public render() {
        return (
            <>
                {this.state.tasks.map((t, i) =>
                    (<React.Fragment key={i}>
                        <AddTaskForm
                            defaultDueDate={this.props.defaultDueDate}
                            task={t.task}
                            taskTypes={this.props.taskTypes}
                            immunisationTypes={this.props.immunisationTypes}
                            immunisationTypeTasks={this.props.immunisationTypeTasks}
                            ohCaseId={this.props.ohCaseId}
                            showErrors={this.props.showErrors}
                            onChange={(task, isValid) => this.setState({
                                tasks: [
                                    ...this.state.tasks.slice(0, i),
                                    { task, isValid },
                                    ...this.state.tasks.slice(i + 1)
                                ]
                            }, () => this.emitOnChange())}
                            medicalTypes={this.props.medicalTypes}
                            caseType={this.props.caseType}
                            hideAssignmentOption={this.props.hideAssignmentOption}
                            assignmentOptionRequired={this.props.assignmentOptionRequired}
                            adminUsers={this.getAdminUserOptions()}
                            medicalOutcomes={this.props.medicalOutcomes}
                            filterMedicalTypes={this.props.filterMedicalTypes}
                        />
                        {this.state.tasks.length > 1 && <>
                            <Form.Field>
                                <Button
                                    icon="remove"
                                    content="Remove Task"
                                    secondary
                                    onClick={() => this.removeTask(i)}
                                    type="button"
                                />
                            </Form.Field>
                            <Divider />
                        </>}
                    </React.Fragment>)
                )}
                {this.props.singleTask || <Form.Field><Button
                    icon="add"
                    content="Add another Task"
                    secondary
                    onClick={this.addTask}
                    type="button"
                /></Form.Field>}
            </>
        );
    }

    public addTask() {
        this.setState({
            tasks: [...this.state.tasks, { task: { taskCode: null, taskType: null, dueDate: null, notes: null }, isValid: false }]
        }, () => this.emitOnChange());
    }

    public removeTask(i: number) {
        this.setState({
            tasks: [
                ...this.state.tasks.slice(0, i),
                ...this.state.tasks.slice(i + 1)
            ]
        }, () => this.emitOnChange());
    }

    private onSavedTasksUpdate() {
        if (this.props.onSavedTasksUpdate) {
            this.props.onSavedTasksUpdate(false);
        }
        this.props.onChange(this.state.tasks);
    }

    private onTasksUpdate() {
        if (this.props.onTasksUpdate) {
            this.props.onTasksUpdate(false);
        }
        this.props.onChange(this.state.tasks);
    }

    private emitOnChange() {
        this.props.onChange(this.state.tasks);
    }
}

const mapStateToProps = (state: AdminUserListModelWithTeamsState): StateProps => {
    return {
        adminUsersWithTeams: adminUsersWithTeamsSelector(state)
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => {
    return {
        loadAdminUsers: () => dispatch(loadAdminUsersAction())
    };
};

export const AddTasksInputs = connect(mapStateToProps, mapDispatchToProps)(AddTasksInputsUnconnected);
